<script lang="ts">
  import type { WeatherContent } from "$lib/elev8Api/contentTypes";
  import {
    getAnimatedWeatherIcon,
    getWeatherIconComponent,
  } from "$lib/weather/weatherIconPicker";
  import { DateHelper } from "$lib/utils/dateHelper";
  import { t } from "$lib/utils/i18n";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";

  type WeatherDayParam =
    | WeatherContent["forecast"][number]
    | NonNullable<WeatherContent["current"]>;

  interface Props {
    weather?: WeatherContent;
  }
  const { weather }: Props = $props();
  const config = getConfigurationContext();

  const unit = "°"; // TODO: restore °C/°F display once layout fits — #237
  let animated = $state(true);
  const forecastStartIndex = $derived(weather?.current ? 1 : 0);
  const forecastDays = $derived(weather?.current ? 2 : 3);

  function getDayOfWeek(offset: number): string {
    if (offset === 0)
      return weather?.current
        ? t("weather.current", config.language)
        : t("weather.today", config.language);
    if (offset === 1) return t("weather.tomorrow", config.language);
    return DateHelper.getDayOfWeekOffsetFromToday(
      offset,
      "long",
      config.language
    );
  }
</script>

{#snippet tempHighLow(high: number, low: number)}
  {@const differentSigns = high < 0 !== low < 0}
  <div class={["high", differentSigns && high >= 0 && "not-negative"]}>
    {high < 0 ? `-${Math.abs(high)}` : high}{unit}
  </div>
  <div
    class={[
      "low",
      differentSigns && low >= 0 && "not-negative",
      high < 0 && low < 0 && "both-negative",
    ]}
  >
    {low < 0 ? `-${Math.abs(low)}` : low}{unit}
  </div>
{/snippet}

{#snippet WeatherDay(day: WeatherDayParam, offset: number)}
  {@const iconKey = day.condition_key ?? ""}
  {@const Icon = getWeatherIconComponent(iconKey)}
  <!-- {@const isCurrent = "current" in day} -->
  <article class="weather-day">
    <div class="temperatures">
      <header class="day-header">{getDayOfWeek(offset)}</header>
      {#if "high" in day && "low" in day}
        {@render tempHighLow(day.high, day.low)}
      {:else if "current" in day}
        <div class="high current">{day.current}{unit}</div>
        <!-- <div>Feels like</div>
      <div class="low">{day.feels_like}{unit}</div> -->
      {/if}
    </div>
    <div class="conditions">
      <div class={["icon", animated && "animated"]}>
        {#if animated}
          <img
            src={getAnimatedWeatherIcon(iconKey)}
            alt={day.condition}
            class="icon__svg"
          />
        {:else}
          <Icon size="100%" color="currentColor" />
        {/if}
      </div>
      <div class="condition-text">{day.condition}</div>
    </div>
  </article>
{/snippet}

<div class="weather-content">
  {#if weather}
    {#if weather.current}
      {@render WeatherDay(weather.current, 0)}
    {/if}
    {#each weather.forecast.slice(forecastStartIndex, forecastStartIndex + forecastDays) as day, index}
      {@render WeatherDay(day, index + forecastStartIndex)}
    {/each}
  {:else}
    <p class="missing-weather">{t("weather.noData", config.language)}</p>
  {/if}
</div>

<style>
  /* ============================================
   * Component Tokens (customize via CSS variables)
   * 
   * Available for override by parent layouts:
   * --weather-border-color: Border color for weather days (default: var(--vc-neutral-light-20))
   * --weather-border-top: Border for top of weather content, allowing for thickness (default: 1px solid var(--weather-border-color))
   * --weather-day-bg: Background for even-numbered days (default: var(--vc-white))
   * --weather-day-bg-alt: Background for odd-numbered days (default: inherit --weather-day-bg)
   * --weather-day-text-alt: Text color for odd-numbered days (default: inherit parent color)
   * --weather-day-border: Border between days (default: 3px solid var(--weather-border-color))
   * --weather-day-padding: Padding inside each day (default: 0 1em 0 1.5em)
   * --weather-day-conditions-margin: Margin around conditions section of each day (default: 0)
   * --weather-temp-low-color: Color for low temperature - optional semantic distinction (default: inherit)
   * --weather-day-icon-width-multiplier: Multiplier for weather icon width (default: 77%)
   * --weather-content-font-size: Base font size for weather content (default: 1rem)
   * 
   * Note: All text colors and fonts inherit from parent layout unless overridden via tokens.
   * ============================================ */

  .weather-content {
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;
    font-size: var(--weather-content-font-size, 1rem);
    /* color inherits from parent layout */
  }

  .weather-day {
    flex: 1;
    display: flex;
    flex-direction: row;
    background-color: var(--weather-day-bg, var(--vc-white));
    border-top: var(
      --weather-border-top,
      1px solid var(--weather-border-color, var(--vc-neutral-light-20))
    );
    border-right: var(
      --weather-day-border,
      3px solid var(--weather-border-color, var(--vc-neutral-light-50))
    );
    padding: var(--weather-day-padding, 0 1em 0 1.5em);

    &:nth-child(odd) {
      background-color: var(
        --weather-day-bg-alt,
        var(--weather-day-bg, var(--vc-white))
      );
      color: var(--weather-day-text-alt, inherit);
    }

    &:first-child {
      border-left: var(
        --weather-day-border,
        3px solid var(--weather-border-color, var(--vc-neutral-light-50))
      );
    }
  }

  .day-header {
    font-size: 1.35em;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-family: var(--vc-font-h1);
    margin-bottom: 0.5em;
  }

  .temperatures {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    white-space: pre;
    line-height: 1;
    padding-top: 1em;
    min-height: min-content;
    min-width: 0; /* allows contents to overflow without increasing width */
    overflow: visible;
  }

  .high {
    font-size: 3.5em;
    font-weight: 700;
    &.current {
      font-size: 4.5em;
    }
    &.not-negative {
      margin-left: 0.1em;
    }
  }

  .low {
    font-size: 2em;
    font-weight: 400;
    margin-left: 0.2rem;
    color: var(--weather-temp-low-color, inherit);
    &.not-negative {
      margin-left: 0.7em;
    }
    &.both-negative {
      margin-left: 0.3em;
    }
  }

  .conditions {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow: visible;
    margin: var(--weather-day-conditions-margin, 0);
  }

  .icon {
    font-size: 4.65em;
    width: 1em;
    margin: 1em auto 0.33em;

    &.animated {
      font-size: 6.4em;
      width: 1em;
      margin: 0.1em auto 0;
      /* Edge definition for pale icons on the white weather bar. */
      filter: drop-shadow(0 2px 3.5px rgba(19, 50, 86, 0.42));
    }
  }

  .condition-text {
    font-size: 1rem; /* *not* relative to weather-content (others are) */
    font-weight: 500;
    text-align: center;
    overflow-wrap: break-word;
    hyphens: auto;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }
</style>
